import { TimelineItemData } from './Timeline';

import { assert } from '../../utils/assert';

import { isOverlapping } from '../../portal/portalUtils';

export function roundTo(value: number, to: number, offset: number = 0) {
    return Math.round((value - offset) / to) * to + offset;
}

export function avoidOverlappingObjects(
    value: TimelineItemData,
    other: TimelineItemData[],
): TimelineItemData {
    const { lane } = value;
    let { start, end } = value;

    const startNum = start.getTime();
    const endNum = end?.getTime() ?? Infinity;

    let overlapping: TimelineItemData[] = getOverlappingTimelineItems(
        value,
        other,
    );
    let exploredArea: TimelineItemData | null = null;
    let trycount = 0;
    while (overlapping.length > 0) {
        exploredArea =
            exploredArea === null
                ? mergeTimelineData(overlapping)
                : mergeTimelineData([...overlapping, exploredArea]);

        trycount++;
        if (trycount > 10) {
            break;
        }

        const middleIntersection =
            ((exploredArea.end?.getTime() ?? Infinity) +
                exploredArea.start.getTime()) /
            2;
        const middleValue = (startNum + (endNum ?? Infinity)) / 2;

        if (exploredArea.end) {
            const size = endNum - startNum;

            if (middleValue > middleIntersection) {
                start = new Date(exploredArea.end);
                end = new Date(start.getTime() + size);
            } else {
                end = new Date(exploredArea.start);
                start = new Date(exploredArea.start.getTime() - size);
            }
        }

        overlapping = getOverlappingTimelineItems(
            {
                start,
                end,
                lane,
            },
            other,
        );
    }

    return {
        start,
        end,
        lane,
    };
}

export function getOverlappingTimelineItems(
    item: TimelineItemData,
    other: TimelineItemData[],
) {
    const { lane, start, end } = item;
    return other.filter(
        (other) =>
            lane === other.lane &&
            isOverlapping(
                [start.getTime(), end?.getTime() ?? Infinity],
                [other.start.getTime(), other.end?.getTime() ?? Infinity],
            ),
    );
}

export function findNextStart(
    onLane: number,
    fromTime: number,
    items: TimelineItemData[],
) {
    const nextStart = items
        .filter(
            (item) => item.lane === onLane && item.start.getTime() >= fromTime,
        )
        .map((item) => {
            return item.start.getTime();
        })
        .reduce((a, b) => Math.min(a, b), Infinity);
    return nextStart;
}

export function findPrevEnd(
    onLane: number,
    fromTime: number,
    items: TimelineItemData[],
) {
    const prevEnd = items
        .filter(
            (item) =>
                item.lane === onLane &&
                item.end &&
                item.end.getTime() <= fromTime,
        )
        .map((item) => {
            assert(item.end);
            return item.end.getTime();
        })
        .reduce((a, b) => Math.max(a, b), 0);
    return prevEnd;
}

export function mergeTimelineData(items: TimelineItemData[]): TimelineItemData {
    return items.reduce((itemA, itemB) => ({
        lane: itemA.lane,
        start:
            itemA.start.getTime() < itemB.start.getTime()
                ? itemA.start
                : itemB.start,
        end:
            (itemA.end?.getTime() ?? Infinity) >
            (itemB.end?.getTime() ?? Infinity)
                ? itemA.end
                : itemB.end,
    }));
}
